import { useCallback, useEffect, useMemo, useRef } from 'react';
import maplibregl, { Map as MLMap, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import Supercluster from 'supercluster';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, MapPin, X } from 'phosphor-react';
import { categoryOf } from '@/constants/categories';
import { CatIcon } from '@/components/ui/CatIcon';
import { createRoot } from 'react-dom/client';
import { formatDayLong, formatTime, hasKnownTime } from '@/lib/dates';
import { upperCity } from '@/lib/city';
import { getResponsiveImageProps } from '@/lib/images';
import type { EventRow } from '@/types/database';

const STYLE = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';
const SPIDER_SOURCE_ID = 'event-map-spider-source';
const SPIDER_LAYER_ID = 'event-map-spider-layer';
const SPIDERFY_THRESHOLD = 8;

interface Props {
  events: (EventRow & { distance_km?: number | null })[];
  center?: { lat: number; lon: number } | null;
  userCenter?: { lat: number; lon: number } | null;
  height?: string | number;
  onEventClick?: (id: string) => void;
  onViewportChange?: (viewport: {
    center: { lat: number; lon: number };
    zoom: number;
    bounds: { north: number; south: number; east: number; west: number };
  }) => void;
  className?: string;
  interactive?: boolean;
  showUserPin?: boolean;
  showAttribution?: boolean;
  focusEventId?: string | null;
  onPopupOpen?: () => void;
  onPopupClose?: () => void;
}

const DEFAULT_CENTER = { lat: 46.2276, lon: 2.2137 };

type EventWithDistance = EventRow & { distance_km?: number | null };
type ManagedMarker = {
  marker: Marker;
  element: HTMLElement;
  cleanup?: () => void;
};
type ClusterFeature = Supercluster.ClusterFeature<{ id: string; kind: EventRow['kind']; title: string }>;
type PointFeature = Supercluster.PointFeature<{ id: string; kind: EventRow['kind']; title: string }>;

export function MapPopupCard({
  event,
  onOpenDetails,
  onClose,
}: {
  event: EventRow & { distance_km?: number | null };
  onOpenDetails: () => void;
  onClose?: () => void;
}) {
  const meta = categoryOf(event.kind);
  const popupImage = getResponsiveImageProps(event.image_url, {
    widths: [240, 320, 480],
    sizes: '320px',
    fallbackWidth: 320,
  });

  return (
    <div className="relative bg-paper-elev p-3 text-left rounded-xl shadow-sheet ring-1 ring-paper-edge" style={{ width: 'min(320px, calc(100vw - 40px))' }}>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 h-9 w-9 rounded-full grid place-items-center bg-paper-elev ring-1 ring-paper-edge"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>
      )}
      {event.image_url ? (
        <img
          src={popupImage?.src ?? event.image_url}
          srcSet={popupImage?.srcSet}
          sizes={popupImage?.sizes}
          alt=""
          className="mb-3 h-28 w-full rounded-md object-cover"
          loading="lazy"
          decoding="async"
        />
      ) : null}

      <div className="t-cap mb-1.5 inline-flex items-center gap-1.5">
        <CatIcon kind={meta.iconKey} size={12} /> {meta.label}
      </div>

      <h3 className="t-ed-md mb-2 pr-8 leading-tight">{event.title}</h3>

      <div className="t-mono-xs text-ink-3">
        {formatDayLong(event.starts_at)}{hasKnownTime(event.starts_at) && ` · ${formatTime(event.starts_at)}`}
      </div>

      {(event.city || event.distance_km != null) ? (
        <div className="mt-1.5 inline-flex items-center gap-1.5 text-ink-3">
          <MapPin size={13} />
          <span className="t-ui-sm">
            {upperCity(event.city) ?? '—'}
            {event.distance_km != null ? ` · ${event.distance_km.toFixed(1)} km` : ''}
          </span>
        </div>
      ) : null}

      {event.description ? (
        <p className="t-ui-sm mt-2 line-clamp-2 text-ink-2">{event.description}</p>
      ) : null}

      <button
        type="button"
        onClick={onOpenDetails}
        className="mt-3 inline-flex h-10 items-center gap-2 rounded-full bg-terre px-4 text-[13px] font-medium text-paper-elev shadow-sm transition hover:opacity-95"
      >
        Voir le détail <ArrowUpRight size={14} weight="bold" />
      </button>
    </div>
  );
}

function isClose(a: { lat: number; lon: number }, b: { lat: number; lon: number }, tolerance = 0.0005) {
  return Math.abs(a.lat - b.lat) <= tolerance && Math.abs(a.lon - b.lon) <= tolerance;
}

function destroyMarker(entry: ManagedMarker | undefined) {
  if (!entry) return;
  entry.cleanup?.();
  entry.marker.remove();
}

function createClusterMarkerElement(count: number) {
  const element = document.createElement('div');
  element.style.cursor = 'pointer';
  const bubble = document.createElement('div');
  bubble.className = 'pin-appear';
  const inner = document.createElement('div');
  inner.style.cssText = "width:42px;height:42px;border-radius:9999px;background:rgb(var(--ink-1));color:rgb(var(--paper-elev));display:grid;place-items:center;font-family:'Geist Mono',monospace;font-weight:600;font-size:13px;box-shadow:0 2px 8px rgba(0,0,0,.18)";
  inner.textContent = String(count);
  bubble.appendChild(inner);
  element.appendChild(bubble);
  return { element };
}

function createEventMarkerElement(kind: EventRow['kind'], animated = true) {
  const element = document.createElement('div');
  element.style.cursor = 'pointer';
  const meta = categoryOf(kind);
  const wrap = document.createElement('div');
  wrap.className = animated ? 'pin-appear' : '';
  wrap.style.cssText = 'width:36px;height:36px;border-radius:9999px;background:rgb(var(--paper-elev));display:grid;place-items:center;box-shadow:0 2px 6px rgba(0,0,0,.18);border:1.5px solid rgb(var(--paper-edge))';
  wrap.style.color = `rgb(var(${meta.colorVar}))`;
  element.appendChild(wrap);
  const root = createRoot(wrap);
  root.render(<CatIcon kind={meta.iconKey} size={18} weight="fill" />);
  return { element, cleanup: () => root.unmount() };
}

export function EventMap({
  events,
  center,
  userCenter,
  height = '100%',
  onEventClick,
  onViewportChange,
  className,
  interactive = true,
  showUserPin = false,
  showAttribution = false,
  focusEventId,
  onPopupOpen,
  onPopupClose,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MLMap | null>(null);
  const markersRef = useRef<Map<string, ManagedMarker>>(new Map());
  const spiderMarkersRef = useRef<Map<string, ManagedMarker>>(new Map());
  const viewportChangeRef = useRef<Props['onViewportChange']>(onViewportChange);
  const onPopupOpenRef = useRef(onPopupOpen);
  const onPopupCloseRef = useRef(onPopupClose);
  const popupRef = useRef<{ popup: maplibregl.Popup; eventId: string; destroy: () => void } | null>(null);
  const spiderClusterRef = useRef<number | null>(null);
  const nav = useNavigate();
  const eventsById = useMemo(() => new Map(events.map((event) => [event.id, event])), [events]);

  const closePopup = useCallback(() => {
    const current = popupRef.current;
    if (!current) return;
    popupRef.current = null;
    current.popup.remove();
    current.destroy();
  }, []);

  const setSpiderLines = useCallback((features: GeoJSON.Feature<GeoJSON.LineString>[]) => {
    const map = mapRef.current;
    if (!map) return;
    const source = map.getSource(SPIDER_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
    if (!source) return;
    source.setData({
      type: 'FeatureCollection',
      features,
    });
  }, []);

  const closeSpiderfy = useCallback(() => {
    spiderClusterRef.current = null;
    spiderMarkersRef.current.forEach((entry) => destroyMarker(entry));
    spiderMarkersRef.current.clear();
    setSpiderLines([]);
  }, [setSpiderLines]);

  const openPopup = useCallback((event: EventRow & { distance_km?: number | null }) => {
    if (!mapRef.current || event.lat == null || event.lon == null || !interactive) return;
    if (popupRef.current?.eventId === event.id) {
      closePopup();
      onPopupCloseRef.current?.();
      return;
    }

    closeSpiderfy();
    closePopup();

    const container = document.createElement('div');
    container.addEventListener('click', (event) => event.stopPropagation());
    const root = createRoot(container);
    let destroyed = false;
    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      root.unmount();
    };

    root.render(
      <MapPopupCard
        event={event}
        onOpenDetails={() => {
          closePopup();
          nav(`/event/${event.id}`);
        }}
      />,
    );

    const popup = new maplibregl.Popup({
      anchor: 'top',
      className: 'event-map-popup',
      closeButton: true,
      closeOnClick: false,
      offset: 12,
    })
      .setLngLat([event.lon, event.lat])
      .setDOMContent(container)
      .setMaxWidth('320px')
      .addTo(mapRef.current);

    popupRef.current = { popup, eventId: event.id, destroy };
    popup.on('close', () => {
      const wasUser = popupRef.current?.popup === popup;
      destroy();
      if (popupRef.current?.popup === popup) popupRef.current = null;
      if (wasUser) onPopupCloseRef.current?.();
    });
    onPopupOpenRef.current?.();
  }, [closePopup, closeSpiderfy, interactive, nav]);

  useEffect(() => {
    if (!focusEventId) return;
    const event = eventsById.get(focusEventId);
    if (event) openPopup(event);
  }, [focusEventId, eventsById, openPopup]);

  const openSpiderfy = useCallback((clusterId: number, center: [number, number], clusterEvents: EventWithDistance[]) => {
    const map = mapRef.current;
    if (!map || !interactive || clusterEvents.length === 0) return;

    closeSpiderfy();

    const centerPoint = map.project({ lng: center[0], lat: center[1] });
    const radius = clusterEvents.length === 2 ? 46 : Math.max(52, 24 + clusterEvents.length * 7);
    const lineFeatures: GeoJSON.Feature<GeoJSON.LineString>[] = [];

    clusterEvents.forEach((event, index) => {
      if (event.lat == null || event.lon == null) return;
      const angle = (-Math.PI / 2) + (index * (Math.PI * 2 / clusterEvents.length));
      const point = {
        x: centerPoint.x + Math.cos(angle) * radius,
        y: centerPoint.y + Math.sin(angle) * radius,
      };
      const lngLat = map.unproject([point.x, point.y]);
      const { element, cleanup } = createEventMarkerElement(event.kind, false);

      element.addEventListener('click', (clickEvent) => {
        clickEvent.stopPropagation();
        if (onEventClick) {
          onEventClick(event.id);
          return;
        }
        openPopup(event);
      });

      const marker = new maplibregl.Marker({ element })
        .setLngLat([lngLat.lng, lngLat.lat])
        .addTo(map);

      spiderMarkersRef.current.set(event.id, { marker, element, cleanup });
      lineFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            center,
            [lngLat.lng, lngLat.lat],
          ],
        },
        properties: {},
      });
    });

    spiderClusterRef.current = clusterId;
    setSpiderLines(lineFeatures);
  }, [closeSpiderfy, interactive, onEventClick, openPopup, setSpiderLines]);

  useEffect(() => {
    viewportChangeRef.current = onViewportChange;
  }, [onViewportChange]);
  useEffect(() => { onPopupOpenRef.current = onPopupOpen; }, [onPopupOpen]);
  useEffect(() => { onPopupCloseRef.current = onPopupClose; }, [onPopupClose]);

  useEffect(() => () => {
    closePopup();
    closeSpiderfy();
  }, [closePopup, closeSpiderfy]);

  useEffect(() => {
    if (!interactive || !popupRef.current) return;
    if (!eventsById.has(popupRef.current.eventId)) closePopup();
  }, [closePopup, eventsById, interactive]);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: ref.current,
      style: STYLE,
      center: center ? [center.lon, center.lat] : [DEFAULT_CENTER.lon, DEFAULT_CENTER.lat],
      zoom: center ? 11 : 5.4,
      attributionControl: false,
      dragRotate: false,
      touchZoomRotate: false,
      pitchWithRotate: false,
      interactive,
    });

    const emitViewport = () => {
      if (!viewportChangeRef.current) return;
      const mapCenter = map.getCenter();
      const bounds = map.getBounds();
      viewportChangeRef.current({
        center: { lat: mapCenter.lat, lon: mapCenter.lng },
        zoom: map.getZoom(),
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        },
      });
    };

    const ensureSpiderLayer = () => {
      if (!map.getSource(SPIDER_SOURCE_ID)) {
        map.addSource(SPIDER_SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }
      if (!map.getLayer(SPIDER_LAYER_ID)) {
        map.addLayer({
          id: SPIDER_LAYER_ID,
          type: 'line',
          source: SPIDER_SOURCE_ID,
          paint: {
            'line-color': 'rgba(33, 37, 41, 0.28)',
            'line-width': 1.5,
          },
        });
      }
    };

    if (showAttribution) {
      map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    }

    const handleLoad = () => {
      ensureSpiderLayer();
      emitViewport();
    };
    const handleMapClick = () => {
      closePopup();
      closeSpiderfy();
    };

    map.on('load', handleLoad);
    map.on('styledata', ensureSpiderLayer);
    map.on('moveend', emitViewport);
    map.on('movestart', closeSpiderfy);
    map.on('click', handleMapClick);
    mapRef.current = map;
    return () => {
      closeSpiderfy();
      map.off('load', handleLoad);
      map.off('styledata', ensureSpiderLayer);
      map.off('moveend', emitViewport);
      map.off('movestart', closeSpiderfy);
      map.off('click', handleMapClick);
      map.remove();
      mapRef.current = null;
    };
  }, [closePopup, closeSpiderfy, interactive, showAttribution]);

  useEffect(() => {
    if (!mapRef.current || !center) return;
    const currentCenter = mapRef.current.getCenter();
    const nextCenter = { lat: center.lat, lon: center.lon };
    if (isClose({ lat: currentCenter.lat, lon: currentCenter.lng }, nextCenter)) return;
    mapRef.current.flyTo({
      center: [center.lon, center.lat],
      zoom: Math.max(mapRef.current.getZoom(), 11),
      duration: 800,
    });
  }, [center?.lat, center?.lon]);

  const points = useMemo(
    () => events
      .filter(e => e.lat != null && e.lon != null)
      .map(e => ({ type: 'Feature' as const, properties: { id: e.id, kind: e.kind, title: e.title }, geometry: { type: 'Point' as const, coordinates: [Number(e.lon), Number(e.lat)] } })),
    [events],
  );

  const cluster = useMemo(() => {
    const sc = new Supercluster<any, any>({ radius: 56, maxZoom: 16 });
    sc.load(points as any);
    return sc;
  }, [points]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    function paint() {
      const m = mapRef.current!;
      const b = m.getBounds();
      const bbox: [number, number, number, number] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
      const z = Math.round(m.getZoom());
      const features = cluster.getClusters(bbox, z) as Array<ClusterFeature | PointFeature>;
      const desiredKeys = new Set<string>();

      const upsertMarker = (key: string, lngLat: [number, number], build: () => ManagedMarker) => {
        desiredKeys.add(key);
        const existing = markersRef.current.get(key);
        if (existing) {
          existing.marker.setLngLat(lngLat);
          return;
        }
        const next = build();
        next.marker.setLngLat(lngLat).addTo(m);
        markersRef.current.set(key, next);
      };

      for (const feature of features) {
        const [lon, lat] = feature.geometry.coordinates as [number, number];
        if ('cluster' in feature.properties && feature.properties.cluster) {
          const clusterId = feature.properties.cluster_id;
          const key = `cluster:${clusterId}`;
          const count = feature.properties.point_count;

          upsertMarker(key, [lon, lat], () => {
            const { element } = createClusterMarkerElement(count);
            if (interactive) {
              element.addEventListener('click', (event) => {
                event.stopPropagation();
                const currentMap = mapRef.current;
                if (!currentMap) return;
                closePopup();
                const expansionZoom = cluster.getClusterExpansionZoom(clusterId);
                if (count <= SPIDERFY_THRESHOLD) {
                  if (spiderClusterRef.current === clusterId) {
                    closeSpiderfy();
                    return;
                  }
                  const revealLeaves = () => {
                    const leaves = cluster.getLeaves(clusterId, count) as PointFeature[];
                    const clusterEvents = leaves
                      .map((leaf) => eventsById.get(leaf.properties.id))
                      .filter((item): item is EventWithDistance => Boolean(item));
                    openSpiderfy(clusterId, [lon, lat], clusterEvents);
                  };
                  const currentCenter = currentMap.getCenter();
                  const needsMove = !isClose(
                    { lat: currentCenter.lat, lon: currentCenter.lng },
                    { lat, lon },
                    0.0001,
                  ) || currentMap.getZoom() < Math.min(expansionZoom, 14.5) - 0.1;

                  if (needsMove) {
                    currentMap.once('moveend', revealLeaves);
                    currentMap.flyTo({
                      center: [lon, lat],
                      zoom: Math.max(currentMap.getZoom(), Math.min(expansionZoom, 14.5)),
                      duration: 450,
                    });
                    return;
                  }

                  revealLeaves();
                  return;
                }

                closeSpiderfy();
                currentMap.flyTo({ center: [lon, lat], zoom: expansionZoom, duration: 600 });
              });
            }
            return {
              marker: new maplibregl.Marker({ element }),
              element,
            };
          });
          continue;
        }

        const key = `event:${feature.properties.id}`;
        upsertMarker(key, [lon, lat], () => {
          const { element, cleanup } = createEventMarkerElement(feature.properties.kind);
          if (interactive) {
            element.addEventListener('click', (clickEvent) => {
              clickEvent.stopPropagation();
              if (onEventClick) {
                onEventClick(feature.properties.id);
                return;
              }
              const clickedEvent = eventsById.get(feature.properties.id);
              if (clickedEvent) openPopup(clickedEvent);
            });
          }
          return {
            marker: new maplibregl.Marker({ element }),
            element,
            cleanup,
          };
        });
      }

      if (showUserPin && userCenter) {
        upsertMarker('user-pin', [userCenter.lon, userCenter.lat], () => {
          const element = document.createElement('div');
          const userBubble = document.createElement('div');
          userBubble.className = 'pin-appear';
          userBubble.innerHTML = `
            <div style="width:18px;height:18px;border-radius:9999px;background:rgb(var(--paper-elev));border:2px solid rgb(var(--paper-elev));box-shadow:0 4px 10px rgba(0,0,0,.18);display:grid;place-items:center">
              <span style="display:block;width:8px;height:8px;border-radius:9999px;background:rgb(var(--terre))"></span>
            </div>
          `;
          element.appendChild(userBubble);
          return {
            marker: new maplibregl.Marker({ element }),
            element,
          };
        });
      }

      markersRef.current.forEach((entry, key) => {
        if (desiredKeys.has(key)) return;
        destroyMarker(entry);
        markersRef.current.delete(key);
      });

      if (spiderClusterRef.current != null && !desiredKeys.has(`cluster:${spiderClusterRef.current}`)) {
        closeSpiderfy();
      }
    }

    paint();
    map.on('moveend', paint);
    return () => {
      map.off('moveend', paint);
      markersRef.current.forEach((entry) => destroyMarker(entry));
      markersRef.current.clear();
      closeSpiderfy();
    };
  }, [closePopup, closeSpiderfy, cluster, eventsById, interactive, onEventClick, openPopup, openSpiderfy, showUserPin, userCenter?.lat, userCenter?.lon]);

  return <div ref={ref} className={className} style={{ height, width: '100%' }} />;
}
